/**
 * Priority Proposal Types
 *
 * A PriorityProposal is the artifact produced by the "Lift" action — the
 * intentional act of converting session-level judgment outputs into a
 * governance artifact that can travel up through Catchball, Review, and
 * Ratification.
 *
 * Design principles:
 * - A proposal aggregates results from one or more sessions within a cycle.
 * - The facilitator adds narrative (rationale + open questions) at Lift time.
 * - Visibility follows chain of custody: holder + tosser during catchball;
 *   holder loses sight after passing; published = broadly visible or via
 *   explicit share.
 */

// ─── Status ───────────────────────────────────────────────────────────────────

/**
 * Lifecycle states of a PriorityProposal as it moves through governance.
 *
 * draft       → being assembled; only the creator can see it
 * catchball   → sent to the ball-tosser; holder + tosser can see and dialog
 * under_review→ tosser is reviewing internally; holder loses direct sight
 * approved    → tosser has approved; awaiting formal ratification
 * ratified    → locked; becomes part of Plan of Record
 * published   → released to the organization
 */
export type PriorityProposalStatus =
  | 'draft'
  | 'catchball'
  | 'under_review'
  | 'approved'
  | 'ratified'
  | 'published';

// ─── Ranked item ──────────────────────────────────────────────────────────────

/**
 * A single ranked/classified item within a proposal.
 * Carries the source session for provenance tracking.
 */
export interface ProposalRankedItem {
  /** Platform item ID */
  itemId: string;
  /** Display title at lift time (snapshot — item may be renamed later) */
  itemTitle: string;
  /** Ordinal rank (1 = highest priority) */
  rank: number;
  /** Numeric score, if the tool produced one */
  score?: number;
  /** Classification category (e.g. "Do", "Schedule") if tool classified */
  category?: string;
  /** Which session produced this result */
  sessionId: string;
  /** True if this item was seeded by the executive who tossed the ball */
  isStrategicallyNominated?: boolean;
}

// ─── Catchball message ────────────────────────────────────────────────────────

/**
 * A message in the holder ↔ tosser catchball dialog.
 * Threaded: parentId links replies to their parent.
 */
export interface ProposalCatchballMessage {
  id: string;
  proposalId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: Date;
  /** ID of the message being replied to, for threading */
  parentId?: string;
}

// ─── Proposal ─────────────────────────────────────────────────────────────────

/**
 * A PriorityProposal — the governance artifact produced by Lifting session
 * results into the ball lane.
 */
export interface PriorityProposal {
  id: string;

  /** The cycle (ball) this proposal belongs to */
  cycleId: string;

  /**
   * The specific Catchball loop this proposal is attached to.
   * A proposal travels with the ball: it is created when judgment work is
   * lifted from the sessions running under a loop, and it moves upward
   * when the ball is passed to the tosser for review.
   */
  loopId?: string;

  /** The track(s) whose sessions contributed to this proposal */
  trackIds: string[];

  /** The session(s) whose results were aggregated into this proposal */
  sessionIds: string[];

  /** Human-readable name for this proposal */
  title: string;

  /**
   * Facilitator-written narrative explaining why the ordering makes sense.
   * Required at Lift time.
   */
  rationale: string;

  /**
   * Questions the facilitator wants the ball-tosser to consider during
   * catchball. Optional but strongly encouraged.
   */
  openQuestions: string;

  /** Aggregated, ordered list of items from the contributing sessions */
  rankedItems: ProposalRankedItem[];

  /** Current status in the governance pipeline */
  status: PriorityProposalStatus;

  /** User ID of the facilitator who performed the Lift */
  createdBy: string;

  /** When the Lift action was performed */
  liftedAt: Date;

  /**
   * User ID of the person who currently holds the ball.
   * Usually the facilitator's team / the level that did the judgment work.
   */
  holderId: string;

  /**
   * User ID of the person who tossed the ball (the Catchball counterpart).
   * This person sees the proposal when it enters catchball.
   */
  tosserId?: string;

  /** When the proposal was ratified (locked) */
  ratifiedAt?: Date;

  /** When the proposal was published */
  publishedAt?: Date;

  /** Scheduled publish date (may differ from ratification date) */
  scheduledPublishDate?: Date;

  /** In-flight catchball dialog between holder and tosser */
  catchballMessages: ProposalCatchballMessage[];

  /**
   * User IDs who have been expressly granted visibility.
   * Used to share the proposal outside the default holder/tosser pair.
   */
  visibleTo: string[];
}

// ─── Store input ──────────────────────────────────────────────────────────────

export interface CreatePriorityProposalInput {
  cycleId: string;
  /** Attaches the proposal to a specific loop so it travels with the ball. */
  loopId?: string;
  trackIds: string[];
  sessionIds: string[];
  title: string;
  rationale: string;
  openQuestions: string;
  rankedItems: ProposalRankedItem[];
  createdBy: string;
  holderId: string;
  /**
   * The person who tossed the ball to the current level. They will receive a
   * notification when the proposal is lifted and can review it in Catchball.
   * When omitted and loopId is provided, the RPC derives this from the loop
   * creator automatically.
   */
  tosserId?: string;
}

// ─── Visibility helpers ───────────────────────────────────────────────────────

/**
 * Determines whether a given user can see this proposal based on chain of
 * custody rules.
 *
 * Rules:
 * - draft      → only the creator
 * - catchball  → holder + tosser
 * - under_review / approved / ratified → tosser only (holder loses sight)
 * - published  → everyone
 * - visibleTo list always grants access regardless of status
 */
export function canUserSeeProposal(
  proposal: PriorityProposal,
  userId: string,
): boolean {
  if (proposal.visibleTo.includes(userId)) return true;

  switch (proposal.status) {
    case 'draft':
      return proposal.createdBy === userId || proposal.holderId === userId;
    case 'catchball':
      return (
        proposal.holderId === userId ||
        proposal.tosserId === userId ||
        proposal.createdBy === userId
      );
    case 'under_review':
    case 'approved':
    case 'ratified':
      return proposal.tosserId === userId;
    case 'published':
      return true;
    default:
      return false;
  }
}

/**
 * Status display configuration for UI badges and pipeline steps.
 */
export const PROPOSAL_STATUS_CONFIG: Record<
  PriorityProposalStatus,
  { label: string; badgeClass: string; description: string }
> = {
  draft: {
    label: 'Draft',
    badgeClass: 'bg-gray-100 text-gray-600',
    description: 'Being assembled',
  },
  catchball: {
    label: 'In Catchball',
    badgeClass: 'bg-blue-100 text-blue-700',
    description: 'Awaiting feedback from above',
  },
  under_review: {
    label: 'Under Review',
    badgeClass: 'bg-amber-100 text-amber-700',
    description: 'Being reviewed at the next level',
  },
  approved: {
    label: 'Accepted',
    badgeClass: 'bg-indigo-100 text-indigo-700',
    description: 'Accepted — awaiting ratification',
  },
  ratified: {
    label: 'Ratified',
    badgeClass: 'bg-green-100 text-green-700',
    description: 'Locked into Plan of Record',
  },
  published: {
    label: 'Published',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    description: 'Released to the organization',
  },
};
